import styles from "./TablePagination.module.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Select from "../ui/Select";

/**
 * Global TablePagination & Limiter Component
 * Adheres to the AsmitA Portal Theme
 *
 * @param {number} totalItems - Total count of records
 * @param {number} page - Current active page (1-indexed)
 * @param {number} limit - Current rows per page
 * @param {function} onPageChange - Callback when page changes (newPage)
 * @param {function} onLimitChange - Callback when limit changes (newLimit)
 * @param {number[]} limitOptions - Selectable limits (default [5, 10, 25, 50, 100])
 * @param {boolean} showLimiter - Whether to display the rows per page dropdown (default true)
 */
export default function TablePagination({
  totalItems = 0,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
  limitOptions = [5, 10, 25, 50, 100],
  showLimiter = true,
  className = "",
}) {
  const safeLimit = Math.max(1, limit || 10);
  const totalPages = Math.max(1, Math.ceil(totalItems / safeLimit));
  const currentPage = Math.min(Math.max(1, page || 1), totalPages);

  const startIdx = totalItems === 0 ? 0 : (currentPage - 1) * safeLimit + 1;
  const endIdx = Math.min(currentPage * safeLimit, totalItems);

  const handlePrev = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLimitSelect = (e) => {
    const newLimit = Number(e.target.value);
    if (onLimitChange) {
      onLimitChange(newLimit);
    }
    if (onPageChange) {
      onPageChange(1);
    }
  };

  return (
    <div className={`${styles.paginationContainer} ${className}`}>
      <div className={styles.leftSection}>
        {showLimiter && (
          <div className={styles.limiterGroup}>
            <span className={styles.limiterLabel}>Rows per page:</span>
            <div style={{ width: "75px" }}>
              <Select
                value={safeLimit}
                onChange={handleLimitSelect}
                options={limitOptions.map((opt) => ({ value: opt, label: String(opt) }))}
                size="sm"
                isSearchable={false}
              />
            </div>
          </div>
        )}

        <span className={styles.infoText}>
          Showing <span className={styles.infoHighlight}>{startIdx}</span> -{" "}
          <span className={styles.infoHighlight}>{endIdx}</span> of{" "}
          <span className={styles.infoHighlight}>{totalItems}</span> entries
        </span>
      </div>

      <div className={styles.rightSection}>
        <button
          type="button"
          onClick={handlePrev}
          disabled={currentPage <= 1}
          className={styles.pageButton}
          title="Previous Page"
          aria-label="Previous Page"
        >
          <ChevronLeft size={16} />
        </button>

        <span className={styles.pageIndicator}>
          Page {currentPage} of {totalPages}
        </span>

        <button
          type="button"
          onClick={handleNext}
          disabled={currentPage >= totalPages || totalItems === 0}
          className={styles.pageButton}
          title="Next Page"
          aria-label="Next Page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
